import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Download, Book, Database, RefreshCw, Loader2, 
  Server, Code, Shield, Users, Calendar, ExternalLink, RotateCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const Documentation = () => {
  const [techDoc, setTechDoc] = useState('');
  const [schemaDoc, setSchemaDoc] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const { getAuthHeaders, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const isSuperAdmin = user?.roles?.some(r => r.role === 'super_admin');

  useEffect(() => {
    fetchDocumentation();
  }, []);

  const fetchDocumentation = async () => {
    setLoading(true);
    try {
      // Fetch technical documentation from API (bypasses cache)
      const techResponse = await fetch(`${API_URL}/api/docs/content/technical`, {
        headers: getAuthHeaders()
      });
      if (techResponse.ok) {
        const techData = await techResponse.json();
        setTechDoc(techData.content);
        if (techData.last_modified) {
          setLastUpdated(new Date(techData.last_modified).toLocaleString());
        }
      }

      // Fetch database schema from API
      const schemaResponse = await fetch(`${API_URL}/api/docs/content/schema`, {
        headers: getAuthHeaders()
      });
      if (schemaResponse.ok) {
        const schemaData = await schemaResponse.json();
        setSchemaDoc(schemaData.content);
      }

    } catch (error) {
      console.error('Error fetching documentation:', error);
      toast.error('Failed to load documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (docType) => {
    setDownloading(docType);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/docs/download-pdf/${docType}`, { headers });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = docType === 'technical' ? 'Technical_Documentation.pdf' : 'Database_Schema.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF downloaded successfully');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadMarkdown = (docType) => {
    const content = docType === 'technical' ? techDoc : schemaDoc;
    const filename = docType === 'technical' ? 'TECHNICAL_DOCUMENTATION.md' : 'DATABASE_SCHEMA.md';
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    toast.success('Markdown file downloaded');
  };

  const handleUpdateDocumentation = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/docs/update`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Documentation updated successfully');
        // Refresh the documentation
        await fetchDocumentation();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update documentation');
      }
    } catch (error) {
      console.error('Error updating documentation:', error);
      toast.error('Failed to update documentation');
    } finally {
      setUpdating(false);
    }
  };

  const QuickStats = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-500 rounded-lg">
            <Server className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-blue-600 dark:text-blue-400">Backend</p>
            <p className="font-semibold text-blue-900 dark:text-blue-100">FastAPI</p>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-green-500 rounded-lg">
            <Code className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-green-600 dark:text-green-400">Frontend</p>
            <p className="font-semibold text-green-900 dark:text-green-100">React 18</p>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-purple-500 rounded-lg">
            <Database className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-purple-600 dark:text-purple-400">Database</p>
            <p className="font-semibold text-purple-900 dark:text-purple-100">MongoDB</p>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="p-2 bg-orange-500 rounded-lg">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs text-orange-600 dark:text-orange-400">Auth</p>
            <p className="font-semibold text-orange-900 dark:text-orange-100">JWT</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2">Loading documentation...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documentation-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Book className="h-7 w-7 text-blue-600" />
            System Documentation
          </h1>
          <p className="text-gray-600 mt-1">
            Technical documentation and database schema for Desknodes
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">
              Last loaded: {lastUpdated}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button 
              variant="default" 
              onClick={handleUpdateDocumentation} 
              disabled={updating}
              className="gap-2 bg-green-600 hover:bg-green-700"
              data-testid="update-docs-btn"
            >
              {updating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4" />
              )}
              Update Documentation
            </Button>
          )}
          <Button variant="outline" onClick={fetchDocumentation} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <QuickStats />

      {/* Documentation Tabs */}
      <Tabs defaultValue="technical" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="technical" className="gap-2">
            <FileText className="h-4 w-4" />
            Technical Documentation
          </TabsTrigger>
          <TabsTrigger value="schema" className="gap-2">
            <Database className="h-4 w-4" />
            Database Schema
          </TabsTrigger>
        </TabsList>

        {/* Technical Documentation Tab */}
        <TabsContent value="technical">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Technical Documentation
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadMarkdown('technical')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Markdown
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleDownloadPDF('technical')}
                  disabled={downloading === 'technical'}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {downloading === 'technical' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm dark:prose-invert max-w-none overflow-auto max-h-[70vh] p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-2xl font-bold text-blue-600 border-b pb-2 mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mt-4 mb-2">{children}</h3>,
                    table: ({children}) => <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600 my-4">{children}</table>,
                    th: ({children}) => <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-left">{children}</th>,
                    td: ({children}) => <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">{children}</td>,
                    code: ({inline, children}) => inline ? 
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono">{children}</code> :
                      <code className="block bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">{children}</code>,
                    pre: ({children}) => <pre className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto my-4">{children}</pre>,
                    blockquote: ({children}) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-600 dark:text-gray-400">{children}</blockquote>,
                    ul: ({children}) => <ul className="list-disc pl-6 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-6 space-y-1">{children}</ol>,
                    hr: () => <hr className="my-6 border-gray-300 dark:border-gray-600" />,
                  }}
                >
                  {techDoc}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Database Schema Tab */}
        <TabsContent value="schema">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5 text-purple-600" />
                Database Schema
              </CardTitle>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDownloadMarkdown('schema')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Markdown
                </Button>
                <Button 
                  size="sm"
                  onClick={() => handleDownloadPDF('schema')}
                  disabled={downloading === 'schema'}
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  {downloading === 'schema' ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm dark:prose-invert max-w-none overflow-auto max-h-[70vh] p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-2xl font-bold text-purple-600 border-b pb-2 mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mt-6 mb-3">{children}</h2>,
                    h3: ({children}) => <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mt-4 mb-2">{children}</h3>,
                    table: ({children}) => <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600 my-4">{children}</table>,
                    th: ({children}) => <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 bg-gray-100 dark:bg-gray-800 font-semibold text-left">{children}</th>,
                    td: ({children}) => <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">{children}</td>,
                    code: ({inline, children}) => inline ? 
                      <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono">{children}</code> :
                      <code className="block bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre">{children}</code>,
                    pre: ({children}) => <pre className="bg-gray-800 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 font-mono text-xs">{children}</pre>,
                    blockquote: ({children}) => <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-600 dark:text-gray-400">{children}</blockquote>,
                    ul: ({children}) => <ul className="list-disc pl-6 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-6 space-y-1">{children}</ol>,
                    hr: () => <hr className="my-6 border-gray-300 dark:border-gray-600" />,
                  }}
                >
                  {schemaDoc}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 rounded-lg border bg-gray-50 dark:bg-gray-800">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                User Roles
              </h4>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Super Admin</li>
                <li>• Office Admin</li>
                <li>• Employee</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg border bg-gray-50 dark:bg-gray-800">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-500" />
                Special Badges
              </h4>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Reviewer</li>
                <li>• Authorizer</li>
                <li>• Approver</li>
                <li>• Marketing Manager</li>
                <li>• Correspondence Manager</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg border bg-gray-50 dark:bg-gray-800">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Database className="h-4 w-4 text-purple-500" />
                Main Collections
              </h4>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• users</li>
                <li>• offices</li>
                <li>• employees</li>
                <li>• letters</li>
                <li>• meetings</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg border bg-gray-50 dark:bg-gray-800">
              <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-orange-500" />
                Recent Updates
              </h4>
              <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Letters module</li>
                <li>• PDF generation</li>
                <li>• Correspondence badge</li>
                <li>• Meeting Pad</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Documentation;
